import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, Settings, Mail, Key, Server, Hash, User, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { settingsApi } from '../api/settingsApi';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

// Zod validation schema
const emailSettingsSchema = z.object({
    smtp_host: z.string()
        .min(1, 'SMTP Host is required')
        .min(2, 'SMTP Host must be at least 2 characters'),
    smtp_port: z.preprocess(
        (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
        z.number({ 
            required_error: 'SMTP Port is required',
            invalid_type_error: 'SMTP Port must be a number' 
        })
            .min(1, 'Port must be greater than 0')
            .max(65535, 'Port must be less than 65536')
    ),
    smtp_user: z.string()
        .min(1, 'SMTP Username is required'),
    smtp_pass: z.string()
        .min(1, 'SMTP Password is required'),
    from_email: z.string()
        .min(1, 'Sender email is required')
        .email('Please enter a valid email address'),
    from_name: z.string()
        .min(1, 'Sender name is required'),
    use_tls: z.boolean().default(true),
});

type EmailSettingsFormData = z.infer<typeof emailSettingsSchema>;

export const EmailSettings = () => {
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const schoolSlug = user?.school?.slug || '';

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [hasExistingConfig, setHasExistingConfig] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset,
    } = useForm<EmailSettingsFormData>({
        resolver: zodResolver(emailSettingsSchema),
        defaultValues: {
            smtp_host: '',
            smtp_port: 587,
            smtp_user: '',
            smtp_pass: '',
            from_email: '',
            from_name: '',
            use_tls: true,
        }
    });

    const useTls = watch('use_tls');
    const smtpUser = watch('smtp_user');

    // Automatically sync SMTP Username (email) to Sender Email Address (From Email)
    useEffect(() => {
        setValue('from_email', smtpUser || '', { shouldValidate: true });
    }, [smtpUser, setValue]);

    useEffect(() => {
        if (schoolSlug) {
            fetchEmailSettings();
        }
    }, [schoolSlug]);

    const fetchEmailSettings = async () => {
        setIsLoading(true);
        try {
            const response = await settingsApi.getEmailConfig(schoolSlug);
            if (response.success && response.data) {
                reset({
                    smtp_host: response.data.smtp_host || '',
                    smtp_port: response.data.smtp_port || 587,
                    smtp_user: response.data.smtp_user || '',
                    smtp_pass: '', // Password is not returned by the API for security
                    from_email: response.data.from_email || '',
                    from_name: response.data.from_name || '',
                    use_tls: response.data.use_tls !== undefined ? response.data.use_tls : true,
                });
                setHasExistingConfig(true);
            }
        } catch (error) {
            console.log('No existing email config found, starting with defaults');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (data: EmailSettingsFormData) => {
        if (!schoolSlug) {
            showToast('School context not found', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const payload: any = {
                smtp_host: data.smtp_host.trim(),
                smtp_port: data.smtp_port,
                smtp_user: data.smtp_user.trim(),
                smtp_pass: data.smtp_pass, // Send password as it is required by the backend
                from_email: data.smtp_user.trim(), // Automatically use the SMTP username email as requested
                from_name: data.from_name.trim(),
                use_tls: data.use_tls,
            };

            const response = await settingsApi.updateEmailConfig(schoolSlug, payload);

            if (response.success) {
                showToast('Email configurations saved successfully!', 'success');
                setHasExistingConfig(true);
                if (response.data) {
                    reset({
                        smtp_host: response.data.smtp_host || '',
                        smtp_port: response.data.smtp_port || 587,
                        smtp_user: response.data.smtp_user || '',
                        smtp_pass: '', // Reset password field to empty after saving
                        from_email: response.data.from_email || '',
                        from_name: response.data.from_name || '',
                        use_tls: response.data.use_tls !== undefined ? response.data.use_tls : true,
                    });
                }
            } else {
                showToast(response.error || 'Failed to save email configurations', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: '1rem 1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>Loading email configurations...</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    // { label: 'Settings', icon: <Settings size={14} /> },
                    { label: 'Email Configuration', icon: <Mail size={14} /> }
                ]}
            />

            <CustomCommonScreenHeader
                title="Email Configuration"
                subtitle="Configure the SMTP settings used by the portal to send outgoing notification emails to parents and employees."
                icon={<Mail size={24} />}
            />

            <div style={{
                maxWidth: '700px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        
                        {/* Host */}
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="SMTP Host"
                                placeholder="e.g. smtp.gmail.com"
                                helperText="The hostname of your SMTP server (e.g. smtp.gmail.com, smtp.mailgun.org)."
                                leftIcon={<Server size={18} />}
                                error={errors.smtp_host?.message}
                                required
                                {...register('smtp_host')}
                            />
                        </div>

                        {/* Port */}
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="SMTP Port"
                                type="number"
                                placeholder="e.g. 587 or 465"
                                helperText="Common standard SMTP ports: 587 (recommended for TLS), 465 (SSL), or 25."
                                leftIcon={<Hash size={18} />}
                                error={errors.smtp_port?.message}
                                required
                                {...register('smtp_port')}
                            />
                        </div>

                        {/* User */}
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="SMTP Username(email)"
                                placeholder="e.g. your-email@gmail.com"
                                helperText="Your SMTP server username (typically the email address used to authenticate)."
                                leftIcon={<User size={18} />}
                                error={errors.smtp_user?.message}
                                required
                                {...register('smtp_user')}
                            />
                        </div>

                        {/* Password */}
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="SMTP Password (App Password)"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your SMTP password"
                                helperText="Required. Enter your SMTP password. (For Gmail/Outlook, you must use a generated App Password)."
                                required
                                leftIcon={<Key size={18} />}
                                rightIcon={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--text-muted)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: 0
                                        }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                }
                                error={errors.smtp_pass?.message}
                                {...register('smtp_pass')}
                            />
                        </div>

                        {/* Divider */}
                        <div style={{ gridColumn: 'span 2', margin: '0.5rem 0', borderTop: '1px solid var(--border-color)' }} />

                        {/* From Email */}
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Sender Email Address (From Email)"
                                type="email"
                                placeholder="e.g. no-reply@school.com"
                                helperText="This address is automatically prefilled and synchronized with your SMTP Username(email)."
                                leftIcon={<Mail size={18} />}
                                error={errors.from_email?.message}
                                required
                                disabled
                                {...register('from_email')}
                            />
                        </div>

                        {/* From Name */}
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Sender Name (From Name)"
                                placeholder="e.g. School Support Center"
                                helperText="The friendly name recipients will see in their inbox (e.g. 'Rabta Notifications' or 'School Administration')."
                                leftIcon={<User size={18} />}
                                error={errors.from_name?.message}
                                required
                                {...register('from_name')}
                            />
                        </div>

                        {/* Use TLS Toggle */}
                        <div style={{ gridColumn: 'span 2', marginTop: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                                TLS Encryption
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setValue('use_tls', !useTls, { shouldValidate: true })}
                                    style={{
                                        width: '46px',
                                        height: '24px',
                                        borderRadius: '12px',
                                        backgroundColor: useTls ? 'var(--accent-primary, #6366f1)' : 'var(--border-color, #e2e8f0)',
                                        border: 'none',
                                        position: 'relative',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        padding: 0
                                    }}
                                >
                                    <span style={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        backgroundColor: '#ffffff',
                                        position: 'absolute',
                                        top: '3px',
                                        left: useTls ? '25px' : '3px',
                                        transition: 'left 0.2s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                                    }} />
                                </button>
                                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                    Use secure TLS connection (Recommended for Port 587)
                                </span>
                            </div>
                        </div>

                        {/* Warning/Info Box */}
                        <div style={{
                            gridColumn: 'span 2',
                            marginTop: '0.5rem',
                            padding: '1rem',
                            background: 'var(--bg-secondary, rgba(99, 102, 241, 0.05))',
                            border: '1px dashed var(--border-color)',
                            borderRadius: '12px',
                            display: 'flex',
                            gap: '0.75rem',
                            alignItems: 'flex-start'
                        }}>
                            <ShieldAlert size={18} style={{ color: 'var(--accent-primary, #6366f1)', flexShrink: 0, marginTop: '2px' }} />
                            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4 }}>
                                <strong>Important Security Note:</strong> Always verify that your SMTP host and port match the correct encryption method. Gmail, Outlook, and other email providers might require configuring app-specific passwords or enabling specific SMTP relay configurations for secure access.
                            </p>
                        </div>

                        {/* Submit Button */}
                        <div style={{
                            gridColumn: 'span 2',
                            marginTop: '2rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isSaving}
                            >
                                Save Configuration
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
